import React, { useState } from 'react';
import { workExperienceData } from '../../data/workExperience';

const WorkExperience = () => {
  const [activeJob, setActiveJob] = useState(null);
  const [visibleDetails, setVisibleDetails] = useState(true);
  const [showShortTerm, setShowShortTerm] = useState(true);
  const [showTechnicalOnly, setShowTechnicalOnly] = useState(false);
  
  // Sort work experience by end date (most recent first)
  const sortedWorkExperience = [...workExperienceData].sort((a, b) => {
    // Extract end dates and convert to comparable format
    const getEndDate = (duration) => {
      // Handle ongoing positions
      if (duration.includes('2025') && !duration.includes(' - ')) {
        return new Date('2025-12-31'); // Assume ongoing until end of 2025
      }
      
      const parts = duration.split(' - ');
      if (parts.length < 2) return new Date();
      
      const endPart = parts[1].trim();
      
      // Parse different date formats
      if (endPart.includes('2025')) return new Date('2025-04-30');
      if (endPart.includes('Aug 2024')) return new Date('2024-08-31');
      if (endPart.includes('Dec 2023')) return new Date('2023-12-31');
      if (endPart.includes('Apr 2023')) return new Date('2023-04-30');
      if (endPart.includes('Feb 2022')) return new Date('2022-02-28');
      if (endPart.includes('Apr 2022')) return new Date('2022-04-30');
      
      return new Date(endPart);
    };
    
    return getEndDate(b.duration) - getEndDate(a.duration);
  });
  
  // Filter work experience based on toggles
  const filteredWorkExperience = sortedWorkExperience.filter(job => {
    const meetsShortTermFilter = showShortTerm || job.durationMonths > 4;
    const meetsTechnicalFilter = !showTechnicalOnly || job.isTechnical;
    
    return meetsShortTermFilter && meetsTechnicalFilter;
  });
  
  // Set initial active job to first in filtered list
  React.useEffect(() => {
    if (filteredWorkExperience.length > 0 && !activeJob) {
      setActiveJob(filteredWorkExperience[0].id);
    }
  }, [filteredWorkExperience, activeJob]);
  
  const handleJobClick = (id) => {
    if (activeJob === id) {
      setVisibleDetails(!visibleDetails);
    } else {
      setActiveJob(id);
      setVisibleDetails(true);
    }
  };
  
  const activeJobData = filteredWorkExperience.find(job => job.id === activeJob);
  
  return (
    <div className="py-16 relative">
      {/* Section header */}
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">Work Experience</h2>
        <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
          My professional journey through various roles and organizations
        </p>
      </div>
      
      {/* Enhanced toggle controls */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-8">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          
          {/* Short-term positions toggle */}
          <div className="flex items-center bg-white dark:bg-gray-800 px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200 shadow-sm hover:shadow-md">
            <div className="flex items-center">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showShortTerm}
                  onChange={(e) => setShowShortTerm(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="relative w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Include Short-term
                </span>
              </label>
            </div>
            <div className="ml-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                {workExperienceData.length - filteredWorkExperience.length} hidden
              </span>
            </div>
          </div>

          {/* Technical positions toggle */}
          <div className="flex items-center bg-white dark:bg-gray-800 px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200 shadow-sm hover:shadow-md">
            <div className="flex items-center">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showTechnicalOnly}
                  onChange={(e) => setShowTechnicalOnly(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="relative w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600"></div>
                <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Technical Only
                </span>
              </label>
            </div>
            <div className="ml-2">
              <svg className="w-4 h-4 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            </div>
          </div>

        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-5 gap-8">
        {/* Timeline navigation - left side */}
        <div className="md:col-span-2">
          <div className="sticky top-24">
            <div className="space-y-1">
              {filteredWorkExperience.map((job) => (
                <button
                  key={job.id}
                  onClick={() => handleJobClick(job.id)}
                  className={`w-full text-left p-4 rounded-lg transition-all duration-300 ${
                    activeJob === job.id 
                      ? 'bg-gray-100 dark:bg-gray-800 border-l-4 border-blue-500' 
                      : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  }`}
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0 mr-4">
                      {/* Replace with actual logo or placeholder */}
                      <div className="w-10 h-10 rounded-md bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400">
                        {job.company.charAt(0)}
                      </div>
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className={`font-medium ${activeJob === job.id ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-200'}`}>
                          {job.role}
                        </h3>
                        {job.isTechnical && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                            <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                            </svg>
                            Tech
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{job.company}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">{job.duration}</p>
                      {/* Duration indicator */}
                      <div className="flex items-center mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          job.durationMonths <= 4 
                            ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400' 
                            : job.durationMonths >= 12 
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                            : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                        }`}>
                          {job.durationMonths} {job.durationMonths === 1 ? 'month' : 'months'}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Job details - right side */}
        <div className="md:col-span-3">
          {activeJobData ? (
            <div className={`bg-white dark:bg-gray-800/50 rounded-xl p-6 border border-gray-200 dark:border-gray-700 transform transition-all duration-500 ${
              visibleDetails ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}>
              <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{activeJobData.role}</h3>
                  {activeJobData.isTechnical && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800">
                      <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                      </svg>
                      Technical Role
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="text-gray-800 dark:text-gray-300">{activeJobData.company}</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                  <span className="text-gray-600 dark:text-gray-400">{activeJobData.location}</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                  <span className="text-gray-600 dark:text-gray-400">{activeJobData.duration}</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                  <span className={`text-sm px-2 py-1 rounded-full ${
                    activeJobData.durationMonths <= 4 
                      ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400' 
                      : activeJobData.durationMonths >= 12 
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                      : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                  }`}>
                    {activeJobData.durationMonths} {activeJobData.durationMonths === 1 ? 'month' : 'months'}
                  </span>
                </div>
                <p className="text-gray-700 dark:text-gray-300">{activeJobData.description}</p>
              </div>

              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Key Responsibilities</h4>
                <ul className="space-y-2">
                  {activeJobData.responsibilities.map((responsibility, index) => (
                    <li key={index} className="flex items-start">
                      <svg className="w-5 h-5 text-blue-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                      <span className="text-gray-700 dark:text-gray-300">{responsibility}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Technologies & Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {activeJobData.technologies.map((tech, index) => (
                    <span key={index} className="px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 rounded-full text-sm border border-blue-100 dark:border-blue-800/30">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Key Achievements</h4>
                <ul className="space-y-2">
                  {activeJobData.achievements.map((achievement, index) => (
                    <li key={index} className="flex items-start">
                      <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700 dark:text-gray-300">{achievement}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Supervisor info if available */}
              {activeJobData.supervisor && (
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium">Supervisor:</span> {activeJobData.supervisor}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800/50 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="text-center text-gray-500 dark:text-gray-400">
                <p>Select a position to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkExperience;